import { useState, useEffect } from "react";
import { apiClient } from "../App";

/**
 * Custom hook for fetching and managing plan limits
 * Reduces duplication between DashboardPage and TemplatesPage
 * 
 * @returns {Object} Plan limits and utilities
 * - limits: { maxTemplates, maxContacts, maxCampaignsPerMonth }
 * - usage: { templates, contacts, campaignsSentThisMonth }
 * - loading: boolean
 * - isAtLimit: (type: 'templates' | 'contacts' | 'campaigns') => boolean
 * - formatLimit: (current, max) => string
 * - refetch: () => void
 */
export const usePlanLimits = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchLimits = async () => {
    try {
      const response = await apiClient.get("/analytics/overview");
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch plan limits:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLimits();
  }, []);

  // Extract limits from response, handling both direct and nested plan object
  const getLimit = (primary, secondary, defaultVal = null) => {
    if (primary !== undefined) return primary;
    if (secondary !== undefined) return secondary;
    return defaultVal;
  };

  const limits = {
    maxTemplates: getLimit(data?.max_templates, data?.plan?.max_templates),
    maxContacts: getLimit(data?.max_contacts, data?.plan?.max_contacts),
    maxCampaignsPerMonth: getLimit(data?.max_campaigns_per_month, data?.plan?.max_campaigns_per_month),
  };

  const usage = {
    templates: data?.templates || 0,
    contacts: data?.contacts?.total || 0,
    campaignsSentThisMonth: data?.campaigns?.sent_this_month || 0,
  };

  /**
   * Format limit display - shows "current/max" or just "current" if unlimited
   */
  const formatLimit = (current, max) => {
    if (max === null || max === undefined) return `${current}`;
    return `${current}/${max}`;
  };

  /**
   * Check if user has reached limit for a specific resource type
   */
  const isAtLimit = (type) => {
    switch (type) {
      case 'templates':
        return limits.maxTemplates !== null && usage.templates >= limits.maxTemplates;
      case 'contacts':
        return limits.maxContacts !== null && usage.contacts >= limits.maxContacts;
      case 'campaigns':
        return limits.maxCampaignsPerMonth !== null && usage.campaignsSentThisMonth >= limits.maxCampaignsPerMonth;
      default:
        return false;
    }
  };

  /**
   * Check if a resource type is unlimited
   */
  const isUnlimited = (type) => {
    switch (type) {
      case 'templates':
        return limits.maxTemplates === null;
      case 'contacts':
        return limits.maxContacts === null;
      case 'campaigns':
        return limits.maxCampaignsPerMonth === null;
      default:
        return false;
    }
  };

  return {
    limits,
    usage,
    loading,
    isAtLimit,
    isUnlimited,
    formatLimit,
    refetch: fetchLimits,
    // Raw data for backwards compatibility
    rawData: data,
  };
};

export default usePlanLimits;
